import {
  Autocomplete,
  Grid,
  InputAdornment,
  TextField as MuiTextField,
  Stack,
  debounce,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { App, Spin, Typography } from "antd";
import { Formik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TextField from "~/components/Extras/TextField";
import GenericFormContainer from "~/components/GenericFormContainer";
import MDBox from "~/components/MDBox";
import MDButton from "~/components/MDButton";
import { roles } from "~/config";
import useAuthorization from "~/hooks/useAuthorization";
import {
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
} from "~/queries/categoryQueries";
import { getDefaultCategory } from "~/services/category.service";
import { shareResultData } from "~/store/slices/utilities";
import {
  MAX_CHARACTER_LENGTH,
  updateInitialValues,
  updateValidationSchema,
} from "./helper";

const CreateCategory = () => {
  // material UI hooks
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  // Redux hooks
  const dispatch = useDispatch();
  // React hooks

  const formRef = useRef(null);
  const [submitButtonLabel, setSubmitButtonLabel] = useState("Submit");
  const [cardHeaderTitle, setCardHeaderTitle] = useState("Create New Category");
  const [description, setDescription] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);

  // Router Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { message } = App.useApp();

  // query mutation hooks
  const createCategoryMutation = useCreateCategoryMutation();
  const updateCategoryMutation = useUpdateCategoryMutation();
  // const categoryData = useParentCategoryQuery();

  const { data, mutate, mutateAsync, reset, isLoading } = useMutation({
    mutationKey: [searchQuery],
    mutationFn: (payload) => getDefaultCategory(payload),
  });

  const { userHasRequiredRole } = useAuthorization([roles.ADMIN]);

  useEffect(() => {
    if (location.state && params) {
      setSubmitButtonLabel("Update");
      setCardHeaderTitle("Update Category");

      // console.log(location.state);
      const { setFieldValue } = formRef.current;
      // formRef.current.values.Cat_ID = location.state.Cat_ID;

      setFieldValue("Default_Cat_Name", location.state.Cat_Name);
      setFieldValue("Cat_ID", location.state.Cat_ID);
      setFieldValue("Sub_Cat_Desc", location.state.Cat_Desc);
      setFieldValue("Cat_Value", location.state.Cat_Value);
    }
  }, [location, params]);

  const handleSubmit = async (values, helpers) => {
    if (location.state && params) {
      // console.log("During the update", params);
      try {
        const updateResponse = await updateCategoryMutation.mutateAsync({
          ...values,
          params: location.state["Sub_Cat_Auto_ID"],
          Is_Active: location.state["Is_Active"],
        });
        // console.log(updateResponse.message);

        message.success(updateResponse.message);
        navigate("/category-master");
      } catch (error) {
        console.log("errroo value", error.response.data.message);
        message.error(error.response.data.message);

        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: error.response.data.message });
        helpers.setSubmitting(false);
      }
    } else {
      try {
        const createResponse = await createCategoryMutation.mutateAsync(values);
        dispatch(
          shareResultData({
            ...createResponse,
            subtitle: "New Category has been created successfully!",
          })
        );
        message.success("Category successfully added.");
        navigate("/category-master");
      } catch (error) {
        console.log("errroo value", error.response.data.message);
        message.error(error.response.data.message);

        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: error.response.data.message });
        helpers.setSubmitting(false);
      }
    }
  };

  function handleAutocompleteChange(value, fieldName, action = false) {
    if (!value?.label) {
      formRef.current.setFieldValue(fieldName.trim(), "");
    } else {
      // do something else
      formRef.current.setFieldValue(fieldName.trim(), value?.label);
      formRef.current.setFieldValue("Cat_ID", value?.value);
    }
  }

  function handleSuggestionInput(searchText) {
    // if (searchText === '' || searchText.length < 4) return;
    setSearchQuery(searchText);
    mutate({ filter: searchText });
  }

  const debounceHandlerForSuggestions = useCallback(
    debounce(handleSuggestionInput, 500),
    []
  );

  const renderForms = async () => {
    const response = await mutateAsync({ filter: searchQuery });
    // if (response.status) {
    //   const { setFieldValue } = formRef.current;
    //   try {
    //     setFieldValue('Loc_Auto_ID', location.state['loc_mstr.Loc_Name']);
    //   } catch (error) {
    //     console.log('error', error);
    //   }
    // }
  };
  useEffect(() => {
    renderForms();

    return () => {
      reset();
    };
  }, []);

  return (
    <GenericFormContainer
      cardHeaderTitle={cardHeaderTitle}
      navigateTo="/category-master"
      p={2}
    >
      <MDBox p={2}>
        <Formik
          initialValues={updateInitialValues}
          validationSchema={updateValidationSchema}
          onSubmit={handleSubmit}
          innerRef={formRef}
        >
          {({
            handleSubmit,
            values,
            handleChange,
            handleBlur,
            handleReset,
            touched,
            errors,
          }) => (
            <form onSubmit={handleSubmit}>
              <Spin
                tip={
                  createCategoryMutation.isLoading
                    ? "Creating..."
                    : "Updating..."
                }
                spinning={
                  createCategoryMutation.isLoading ||
                  updateCategoryMutation.isLoading
                }
              >
                <Grid container spacing={2}>
                  <Grid item lg={3} md={3} sm={6} xs={12}>
                    <Autocomplete
                      readOnly={location.state && params?.catId}
                      size="small"
                      id="outlined-adornment-assetAutocomplete"
                      label="Select Default Category *"
                      name="Default_Cat_Name"
                      open={open}
                      onOpen={() => {
                        setOpen(true);
                      }}
                      onClose={() => {
                        setOpen(false);
                      }}
                      value={values?.Default_Cat_Name ?? ""}
                      options={
                        data?.data?.map((item) => ({
                          value: item.Cat_Auto_ID,
                          label: item.Cat_Name,
                        })) || []
                      }
                      onChange={(event, newValue) => {
                        handleChange(event);
                        handleAutocompleteChange(newValue, "Default_Cat_Name");
                      }}
                      onInputChange={(event, value) =>
                        debounceHandlerForSuggestions(value)
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.label === value
                      }
                      onBlur={handleBlur}
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          id="outlined-adornment-assetAutocomplete"
                          name="Default_Cat_Name"
                          label="Select Default Category *"
                          error={Boolean(
                            touched.Default_Cat_Name && errors.Default_Cat_Name
                          )}
                          helperText={
                            touched.Default_Cat_Name && errors.Default_Cat_Name
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {isLoading ? <Spin /> : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                    {/* <SelectField
                name="Cat_ID"
                label="Select Default Category"
                options={categoryData.data?.data?.map((item) => ({
                  value: item.Cat_Auto_ID,
                  label: item.Cat_Name,
                }))}
                isRequired
              /> */}
                  </Grid>
                  <Grid item lg={3} md={3} sm={6} xs={12}>
                    <TextField
                      size="small"
                      label="Sub Category Name"
                      name="Cat_Value"
                      isRequired
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <TextField
                      size="small"
                      label="Description"
                      name="Sub_Cat_Desc"
                      isRequired
                      onChange={(e) => {
                        setDescription(e.target.value);
                        handleChange(e);
                      }}
                      inputProps={{ maxLength: MAX_CHARACTER_LENGTH }}
                      endAdornment={
                        <InputAdornment position="end">
                          <Typography.Text>{`${
                            description?.length ?? 0
                          }/${MAX_CHARACTER_LENGTH}`}</Typography.Text>
                        </InputAdornment>
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    sx={{
                      display: "flex",
                      justifyContent: matchesXs ? "flex-end" : "end",
                    }}
                  >
                    <Stack
                      direction={matchesXs ? "column-reverse" : "row"}
                      spacing={2}
                      useFlexGap
                    >
                      <MDButton
                        sx={{ px: 5 }}
                        variant="contained"
                        color="error"
                        size="large"
                        type="reset"
                        onClick={(e) => {
                          if (
                            location.state &&
                            location.pathname.includes("update")
                          ) {
                            navigate(-1);
                          } else {
                            handleReset(e);
                          }
                        }}
                      >
                        Cancel
                      </MDButton>
                      {userHasRequiredRole && (
                        <MDButton
                          sx={{ px: 6 }}
                          type="submit"
                          fullWidth
                          variant="contained"
                          color="success"
                          size="large"
                          onClick={() =>
                            console.log("ON SUBMIT", formRef.current)
                          }
                        >
                          {submitButtonLabel}
                        </MDButton>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Spin>
            </form>
          )}
        </Formik>
      </MDBox>
    </GenericFormContainer>
  );
};

export default CreateCategory;
